/* The dataset "Data" grid: real rows and columns, paged on the server.
 * Sticky header with column types, row numbers, 50/100/200/500 rows per
 * page, first/prev/next/last, quick search, click-to-sort, horizontal scroll,
 * number/date formatting, and the real error when the source can't be read. */
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Calendar, ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, ChevronsUpDown, Hash, RefreshCw, SearchX, ToggleLeft, Type as TypeIcon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { rowsApi, type Area, type ColumnKind, type RowsColumn } from "../api/v21";
import { Banner, Button, EmptyState, SearchInput, Segmented, SkeletonRows, formatRelative } from "../ds";
import "./datagrid.css";

const PAGE_SIZES = [50, 100, 200, 500] as const;
const nf = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const intf = new Intl.NumberFormat();
const ISO_DATE = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?)?$/;

const TYPE_ICON: Record<ColumnKind, typeof Hash> = { number: Hash, date: Calendar, text: TypeIcon, boolean: ToggleLeft };
const TYPE_LABEL: Record<ColumnKind, string> = { number: "Number", date: "Date", text: "Text", boolean: "Yes / no" };

export function formatCell(value: unknown, kind: ColumnKind): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") return nf.format(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string" && (kind === "date" ? /^\d{4}-\d{2}-\d{2}/ : ISO_DATE).test(value)) {
    const d = new Date(value.length === 10 ? `${value}T00:00:00` : value.replace(" ", "T"));
    if (!Number.isNaN(d.getTime())) {
      const date = d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
      return value.length > 10 && !/[ T]00:00(:00)?(\.0+)?(Z|[+-]00:00)?$/.test(value) ? `${date} ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}` : date;
    }
  }
  return String(value);
}

function highlight(text: string, needle: string): ReactNode {
  if (!needle) return text;
  const i = text.toLowerCase().indexOf(needle.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="dg-mark">{text.slice(i, i + needle.length)}</mark>
      {text.slice(i + needle.length)}
    </>
  );
}

export function DatasetRowsGrid({ area, datasetId, toolbarExtra }: { area: Area; datasetId: string; toolbarExtra?: ReactNode }) {
  const [pageSize, setPageSize] = useState<number>(() => {
    try {
      const v = Number(window.localStorage.getItem("mrp-rows-page-size"));
      return (PAGE_SIZES as readonly number[]).includes(v) ? v : 100;
    } catch {
      return 100;
    }
  });
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<{ field: string; direction: "asc" | "desc" } | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const t = window.setTimeout(() => { setSearch(searchText.trim()); setPage(0); }, 300);
    return () => window.clearTimeout(t);
  }, [searchText]);
  useEffect(() => {
    try { window.localStorage.setItem("mrp-rows-page-size", String(pageSize)); } catch { /* private mode */ }
  }, [pageSize]);

  const q = useQuery({
    queryKey: ["dataset-rows", area, datasetId, pageSize, page, search, sort?.field, sort?.direction, refreshTick],
    queryFn: () => rowsApi.page(area, datasetId, { offset: page * pageSize, limit: pageSize, search: search || undefined, sort, refresh: refreshTick > 0 && page === 0 }),
    placeholderData: (prev) => (prev && prev.columns.length ? prev : undefined),
    retry: false,
    staleTime: 30_000,
  });
  const d = q.data;
  const cols: RowsColumn[] = d?.columns ?? [];
  const total = d?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const first = total ? (d?.offset ?? 0) + 1 : 0;
  const last = Math.min(total, (d?.offset ?? 0) + (d?.rows.length ?? 0));

  const toggleSort = (field: string) => {
    setPage(0);
    setSort((s) => (s?.field !== field ? { field, direction: "asc" } : s.direction === "asc" ? { field, direction: "desc" } : null));
  };

  return (
    <div>
      <div className="dg-toolbar">
        <SearchInput value={searchText} onChange={setSearchText} placeholder="Search every column" width={260} />
        <span className="ds-small ds-muted">Rows per page</span>
        <Segmented<string> size="sm" value={String(pageSize)} onChange={(v) => { setPageSize(Number(v)); setPage(0); }} options={PAGE_SIZES.map((n) => ({ value: String(n), label: String(n) }))} />
        <div className="ds-grow" />
        {toolbarExtra}
        <Button size="sm" variant="ghost" icon={RefreshCw} loading={q.isFetching && refreshTick > 0} onClick={() => { setPage(0); setRefreshTick((n) => n + 1); }} title="Read the source again">Reload</Button>
      </div>

      {d?.status === "unavailable" ? (
        <Banner tone="danger" title="Couldn't read this data right now">{d.error || "The data source didn't answer."} Nothing is wrong with your reports' saved settings — try Reload in a minute.</Banner>
      ) : d?.status === "delayed" ? (
        <div style={{ marginBottom: 12 }}><Banner tone="warning" title="Showing the last good copy">The source couldn't be read just now ({d.error}). These rows are from {formatRelative(d.last_refreshed)}.</Banner></div>
      ) : null}
      {d?.truncated ? <div style={{ marginBottom: 12 }}><Banner tone="info" title={`Showing the first ${intf.format(d.max_rows)} rows`}>The source has more rows than the grid reads at once. Reports still use every row.</Banner></div> : null}
      {d?.reviewSource === "derived" ? <div style={{ marginBottom: 12 }}><Banner tone="info" title="Review mode">These rows come from what the reports show for this dataset (the snapshot has no raw rows yet).</Banner></div> : null}

      {q.isLoading && !d ? (
        <SkeletonRows rows={8} />
      ) : q.isError ? (
        <Banner tone="danger" title="Couldn't load the rows">{q.error instanceof Error ? q.error.message : null}</Banner>
      ) : d && d.status !== "unavailable" ? (
        cols.length === 0 ? (
          <EmptyState icon={TypeIcon} title="No columns yet" description="This dataset has no visible fields. Refresh it, or make some fields visible on the Fields tab." />
        ) : total === 0 ? (
          <EmptyState icon={SearchX} title={search ? `Nothing matches “${search}”` : "No rows"} description={search ? "Try fewer letters, or clear the search." : "The source returned an empty table."} action={search ? <Button onClick={() => setSearchText("")}>Clear search</Button> : undefined} />
        ) : (
          <>
            <div className={`dg-wrap${q.isFetching ? " is-loading" : ""}`}>
              <table className="dg-table" aria-rowcount={total}>
                <thead>
                  <tr>
                    <th className="dg-rownum" scope="col">#</th>
                    {cols.map((c) => {
                      const Icon = TYPE_ICON[c.kind] ?? TypeIcon;
                      const active = sort?.field === c.name;
                      const SortIcon = !active ? ChevronsUpDown : sort?.direction === "asc" ? ArrowUp : ArrowDown;
                      return (
                        <th key={c.name} scope="col" className={c.kind === "number" ? "is-num" : undefined} aria-sort={active ? (sort?.direction === "asc" ? "ascending" : "descending") : "none"}>
                          <button type="button" onClick={() => toggleSort(c.name)} title={`${c.display_name} · ${TYPE_LABEL[c.kind] ?? "Text"}${c.display_name !== c.name ? ` · column “${c.name}”` : ""} — click to sort`}>
                            <Icon size={13} className="dg-type" aria-label={TYPE_LABEL[c.kind]} />
                            <span>{c.display_name}</span>
                            <SortIcon size={12} className="dg-sort" />
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {d.rows.map((r, i) => (
                    <tr key={d.offset + i}>
                      <td className="dg-rownum">{intf.format(d.offset + i + 1)}</td>
                      {cols.map((c, ci) => {
                        const v = r[ci];
                        const text = formatCell(v, c.kind);
                        const isNull = v === null || v === undefined || v === "";
                        return (
                          <td key={c.name} className={[c.kind === "number" ? "is-num" : "", isNull ? "is-null" : ""].join(" ").trim() || undefined} title={isNull ? undefined : String(v)}>
                            {isNull ? text : highlight(text, search)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="dg-pager">
              <span className="dg-meta">
                Rows {intf.format(first)}–{intf.format(last)} of {intf.format(total)}
                {search && d.total_rows !== total ? ` (filtered from ${intf.format(d.total_rows)})` : ""}
                {d.last_refreshed ? ` · read ${formatRelative(d.last_refreshed)}` : ""}
              </span>
              <div className="dg-pager__nav" role="navigation" aria-label="Pages">
                <Button size="sm" variant="ghost" icon={ChevronFirst} disabled={page === 0} onClick={() => setPage(0)} aria-label="First page" title="First page" />
                <Button size="sm" variant="ghost" icon={ChevronLeft} disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} aria-label="Previous page" title="Previous page" />
                <span className="dg-pager__page">Page {intf.format(Math.floor((d.offset ?? 0) / pageSize) + 1)} of {intf.format(pages)}</span>
                <Button size="sm" variant="ghost" icon={ChevronRight} disabled={page >= pages - 1} onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} aria-label="Next page" title="Next page" />
                <Button size="sm" variant="ghost" icon={ChevronLast} disabled={page >= pages - 1} onClick={() => setPage(pages - 1)} aria-label="Last page" title="Last page" />
              </div>
            </div>
          </>
        )
      ) : null}
    </div>
  );
}
